import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import { api, formatApiError } from "@/lib/api";
import { ReasonLogo, ReasonFooter } from "@/components/brand/ReasonLogo";

const MD = {
  h1: (p) => <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight mt-8 mb-3" {...p} />,
  h2: (p) => <h2 className="font-display text-xl font-semibold tracking-tight mt-8 mb-2 border-b border-border pb-1" {...p} />,
  h3: (p) => <h3 className="font-semibold text-base mt-5 mb-1.5" {...p} />,
  p: (p) => <p className="text-sm leading-relaxed text-muted-foreground my-3" {...p} />,
  ul: (p) => <ul className="list-disc pl-6 space-y-1.5 text-sm text-muted-foreground my-3" {...p} />,
  ol: (p) => <ol className="list-decimal pl-6 space-y-1.5 text-sm text-muted-foreground my-3" {...p} />,
  li: (p) => <li className="leading-relaxed" {...p} />,
  a: (p) => <a className="text-primary font-medium hover:underline" target="_blank" rel="noreferrer" {...p} />,
  strong: (p) => <strong className="text-foreground font-semibold" {...p} />,
  em: (p) => <em className="italic" {...p} />,
  hr: () => <hr className="my-6 border-border" />,
};

export default function PrivacyPolicyPage() {
  const [doc, setDoc] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get("/legal/privacy_policy/current")
      .then((r) => setDoc(r.data))
      .catch((e) => setError(formatApiError(e)));
  }, []);

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="privacy-policy-page">
      <header className="border-b border-border bg-card">
        <div className="max-w-3xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/" data-testid="privacy-home-link"><ReasonLogo size={30} /></Link>
          <Link to="/login" className="text-sm text-primary font-medium hover:underline">Sign in</Link>
        </div>
      </header>

      <main className="flex-1 w-full max-w-3xl mx-auto px-5 py-8">
        {error && (
          <div className="text-sm text-destructive" data-testid="privacy-error">{error}</div>
        )}
        {!doc && !error && (
          <div className="text-sm text-muted-foreground">Loading privacy policy…</div>
        )}
        {doc && (
          <article>
            <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground" data-testid="privacy-meta">
              <span className="rounded-md border border-border px-2.5 py-1">Version {doc.version}</span>
              <span className="rounded-md border border-border px-2.5 py-1">Effective {doc.effective_date}</span>
            </div>
            <div className="mt-2" data-testid="privacy-content">
              <ReactMarkdown components={MD}>{doc.content_markdown}</ReactMarkdown>
            </div>
            <div className="mt-8 border-t border-border pt-4">
              <Link to="/data-protection-policy" className="text-primary font-medium hover:underline text-sm" data-testid="privacy-dpp-link">
                Read our full Data Protection Policy →
              </Link>
              <span className="mx-2 text-muted-foreground">·</span>
              <Link to="/terms-of-service" className="text-primary font-medium hover:underline text-sm" data-testid="privacy-tos-link">
                Terms of Service →
              </Link>
            </div>
          </article>
        )}
      </main>

      <footer className="border-t border-border bg-card">
        <div className="max-w-3xl mx-auto px-5 py-4">
          <ReasonFooter />
        </div>
      </footer>
    </div>
  );
}
